import { useEffect, useRef, useState } from "react";
import { percentile } from "../lib/perf";

/** How many pointer moves the readout averages over. */
const WINDOW = 90;
/** How often it redraws. Often enough to watch, rarely enough to cost nothing. */
const REFRESH_MS = 500;

interface PerfReadoutProps {
  /** Words on the page, since every cost here grows with the article. */
  words: number;
}

/**
 * How long after the browser made a pointer move this page got to handle it.
 *
 * That lag is what a reader feels as the stroke dragging behind their finger,
 * and it is the one number worth reading off a real device: it counts whatever
 * the main thread is busy with, whether or not the page can see what that is.
 * Shown only with `?perf=1`.
 */
const PerfReadout = ({ words }: PerfReadoutProps) => {
  const samplesRef = useRef<number[]>([]);
  const movesRef = useRef(0);
  const [{ median, worst, moves }, setShown] = useState({
    median: 0,
    worst: 0,
    moves: 0,
  });

  useEffect(() => {
    const record = (event: PointerEvent) => {
      const samples = samplesRef.current;
      samples.push(performance.now() - event.timeStamp);
      if (samples.length > WINDOW) samples.shift();
      // Counted here, as each move arrives. Adding up the rolling window on
      // every redraw instead made this climb by a windowful a second forever
      // after the reader stopped — which read as a leak in the app, and was
      // one in the instrument.
      movesRef.current += 1;
    };
    // Passive and on the window, so the readout can never itself delay a stroke.
    window.addEventListener("pointermove", record, { passive: true, capture: true });

    const timer = window.setInterval(() => {
      const samples = samplesRef.current;
      setShown({
        median: percentile(samples, 0.5),
        worst: percentile(samples, 0.95),
        moves: movesRef.current,
      });
    }, REFRESH_MS);

    return () => {
      window.removeEventListener("pointermove", record, true);
      window.clearInterval(timer);
    };
  }, []);

  return (
    <div
      data-testid="perf-readout"
      style={{
        position: "fixed",
        top: "6px",
        left: "8px",
        zIndex: 2,
        padding: "4px 8px",
        borderRadius: "4px",
        backgroundColor: "rgba(0, 0, 0, 0.72)",
        color: "white",
        font: "11px ui-monospace, monospace",
        pointerEvents: "none",
      }}
    >
      lag {median}ms · worst {worst}ms · {words} words · {moves} moves
    </div>
  );
};

export default PerfReadout;
